/** @module @liquidefy/code-editor/adapters/adapter-lsp @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  Position,
  Range,
  Selection,
  CompletionKind,
  CompletionItem,
  CompletionTriggerKind,
  CompletionContext,
  DiagnosticSeverity,
  DiagnosticInfo,
  DiagnosticTag,
  CodeAction,
  CodeActionKind,
  SymbolKind,
  DocumentSymbol,
  EditOperation,
  HoverInfo,
  SignatureHelp,
  SignatureInfo,
  ParameterInfo,
  LocationLink,
  FormatOptions,
  LSPClientCapabilities,
  LSPClientConfig,
  EditorConfig,
  LanguageId,
} from '../code-editor-types.js';

import {
  ok,
  err,
  asCommandId,
  asCodeActionId,
} from '../code-editor.js';

// ═══ §2 POSITION & RANGE — bidirectional LSP position/range/location mapping ═══

export interface LSPPosition {
  readonly line: number;
  readonly character: number;
}

export interface LSPRange {
  readonly start: LSPPosition;
  readonly end: LSPPosition;
}

export interface LSPLocation {
  readonly uri: string;
  readonly range: LSPRange;
}

export interface LSPLocationLink {
  readonly originSelectionRange?: LSPRange;
  readonly targetUri: string;
  readonly targetRange: LSPRange;
  readonly targetSelectionRange: LSPRange;
}

export function positionToLSP(position: Position): LSPPosition {
  return { line: position.line, character: position.column };
}

export function positionFromLSP(lspPos: LSPPosition): Position {
  return { line: lspPos.line, column: lspPos.character };
}

export function rangeToLSP(range: Range): LSPRange {
  return {
    start: positionToLSP(range.start),
    end: positionToLSP(range.end),
  };
}

export function rangeFromLSP(lspRange: LSPRange): Range {
  return {
    start: positionFromLSP(lspRange.start),
    end: positionFromLSP(lspRange.end),
  };
}

export function locationToLSP(uri: string, range: Range): LSPLocation {
  return { uri, range: rangeToLSP(range) };
}

export function locationFromLSP(lspLocation: LSPLocation): { uri: string; range: Range } {
  return {
    uri: lspLocation.uri,
    range: rangeFromLSP(lspLocation.range),
  };
}

export function locationLinkToLSP(link: LocationLink): LSPLocationLink {
  return {
    originSelectionRange: link.originSelectionRange
      ? rangeToLSP(link.originSelectionRange)
      : undefined,
    targetUri: link.targetUri,
    targetRange: rangeToLSP(link.targetRange),
    targetSelectionRange: rangeToLSP(link.targetSelectionRange),
  };
}

export function locationLinkFromLSP(lspLink: LSPLocationLink): LocationLink {
  return {
    originSelectionRange: lspLink.originSelectionRange
      ? rangeFromLSP(lspLink.originSelectionRange)
      : undefined,
    targetUri: lspLink.targetUri,
    targetRange: rangeFromLSP(lspLink.targetRange),
    targetSelectionRange: rangeFromLSP(lspLink.targetSelectionRange),
  };
}

// ═══ §3 DIAGNOSTIC MAPPING — diagnostics, severity, tags, code actions ═══

export function severityToLSP(severity: DiagnosticSeverity): number {
  switch (severity) {
    case 'error': return 1;
    case 'warning': return 2;
    case 'info': return 3;
    case 'hint': return 4;
  }
}

export function severityFromLSP(lspSeverity: number): DiagnosticSeverity {
  switch (lspSeverity) {
    case 1: return 'error';
    case 2: return 'warning';
    case 3: return 'info';
    case 4: return 'hint';
    default: return 'info';
  }
}

export function diagnosticTagToLSP(tag: DiagnosticTag): number {
  switch (tag) {
    case 'unnecessary': return 1;
    case 'deprecated': return 2;
  }
}

export function diagnosticTagFromLSP(lspTag: number): DiagnosticTag | null {
  switch (lspTag) {
    case 1: return 'unnecessary';
    case 2: return 'deprecated';
    default: return null;
  }
}

export function diagnosticToLSP(diagnostic: DiagnosticInfo): Record<string, unknown> {
  return {
    range: rangeToLSP(diagnostic.range),
    severity: severityToLSP(diagnostic.severity),
    code: diagnostic.code,
    source: diagnostic.source,
    message: diagnostic.message,
    tags: (diagnostic.tags ?? []).map(diagnosticTagToLSP),
    relatedInformation: (diagnostic.relatedInformation ?? []).map(ri => ({
      location: { uri: ri.uri, range: rangeToLSP(ri.range) },
      message: ri.message,
    })),
  };
}

export function diagnosticFromLSP(lspDiag: Record<string, unknown>): DiagnosticInfo {
  const lspRange = lspDiag['range'] as LSPRange;
  const lspTags = (lspDiag['tags'] as number[] | undefined) ?? [];
  const lspRelated = (lspDiag['relatedInformation'] as Record<string, unknown>[] | undefined) ?? [];

  return {
    range: rangeFromLSP(lspRange),
    severity: severityFromLSP(lspDiag['severity'] as number),
    code: lspDiag['code'] as string | number | undefined,
    source: lspDiag['source'] as string | undefined,
    message: lspDiag['message'] as string,
    tags: lspTags
      .map(diagnosticTagFromLSP)
      .filter((t): t is DiagnosticTag => t !== null),
    relatedInformation: lspRelated.map(ri => {
      const loc = ri['location'] as LSPLocation;
      return {
        uri: loc.uri,
        range: rangeFromLSP(loc.range),
        message: ri['message'] as string,
      };
    }),
  };
}

const CODE_ACTION_KIND_MAP: ReadonlyMap<CodeActionKind, string> = new Map([
  ['quickfix', 'quickfix'],
  ['refactor', 'refactor'],
  ['refactor.extract', 'refactor.extract'],
  ['refactor.inline', 'refactor.inline'],
  ['refactor.rewrite', 'refactor.rewrite'],
  ['source', 'source'],
  ['source.organizeImports', 'source.organizeImports'],
  ['source.fixAll', 'source.fixAll'],
]);

const CODE_ACTION_KIND_REVERSE: ReadonlyMap<string, CodeActionKind> = new Map(
  Array.from(CODE_ACTION_KIND_MAP.entries()).map(([k, v]) => [v, k]),
);

export function codeActionKindToLSP(kind: CodeActionKind): string {
  return CODE_ACTION_KIND_MAP.get(kind) ?? kind;
}

export function codeActionKindFromLSP(lspKind: string): CodeActionKind {
  return CODE_ACTION_KIND_REVERSE.get(lspKind) ?? 'quickfix';
}

export function codeActionToLSP(action: CodeAction): Record<string, unknown> {
  return {
    title: action.title,
    kind: codeActionKindToLSP(action.kind),
    diagnostics: (action.diagnostics ?? []).map(diagnosticToLSP),
    isPreferred: action.isPreferred ?? false,
    edit: action.edit
      ? {
          changes: {},
          documentChanges: (action.edit).map(op => ({
            range: rangeToLSP(op.range),
            newText: op.text,
          })),
        }
      : undefined,
    command: action.command
      ? { title: action.command.title, command: action.command.id }
      : undefined,
  };
}

export function codeActionFromLSP(lspAction: Record<string, unknown>): CodeAction {
  const lspDiagnostics = (lspAction['diagnostics'] as Record<string, unknown>[] | undefined) ?? [];
  const lspCommand = lspAction['command'] as Record<string, unknown> | undefined;

  return {
    id: asCodeActionId(`ca_${Date.now()}`),
    title: lspAction['title'] as string,
    kind: codeActionKindFromLSP((lspAction['kind'] as string) ?? 'quickfix'),
    diagnostics: lspDiagnostics.map(diagnosticFromLSP),
    isPreferred: lspAction['isPreferred'] as boolean | undefined,
    command: lspCommand
      ? {
          id: asCommandId(lspCommand['command'] as string),
          title: (lspCommand['title'] as string) ?? '',
        }
      : undefined,
  };
}

// ═══ §4 COMPLETION MAPPING — completion items and kinds ═══

export const LSP_COMPLETION_KIND_MAP: ReadonlyMap<CompletionKind, number> = new Map([
  ['text', 1],
  ['method', 2],
  ['function', 3],
  ['constructor', 4],
  ['field', 5],
  ['variable', 6],
  ['class', 7],
  ['interface', 8],
  ['module', 9],
  ['property', 10],
  ['unit', 11],
  ['value', 12],
  ['enum', 13],
  ['keyword', 14],
  ['snippet', 15],
  ['color', 16],
  ['file', 17],
  ['reference', 18],
  ['folder', 19],
  ['enum-member', 20],
  ['constant', 21],
  ['struct', 22],
  ['event', 23],
  ['operator', 24],
  ['type-parameter', 25],
]);

const LSP_COMPLETION_KIND_REVERSE: ReadonlyMap<number, CompletionKind> = new Map(
  Array.from(LSP_COMPLETION_KIND_MAP.entries()).map(([k, v]) => [v, k]),
);

export function completionKindToLSP(kind: CompletionKind): number {
  return LSP_COMPLETION_KIND_MAP.get(kind) ?? 1;
}

export function completionKindFromLSP(lspKind: number): CompletionKind {
  return LSP_COMPLETION_KIND_REVERSE.get(lspKind) ?? 'text';
}

export function completionTriggerToLSP(trigger: CompletionTriggerKind): number {
  switch (trigger) {
    case 'invoke': return 1;
    case 'character': return 2;
    case 'incomplete': return 3;
  }
}

export function completionTriggerFromLSP(lspTrigger: number): CompletionTriggerKind {
  switch (lspTrigger) {
    case 1: return 'invoke';
    case 2: return 'character';
    case 3: return 'incomplete';
    default: return 'invoke';
  }
}

export function completionItemToLSP(item: CompletionItem): Record<string, unknown> {
  return {
    label: item.label,
    kind: completionKindToLSP(item.kind),
    detail: item.detail,
    documentation: item.documentation
      ? { kind: 'markdown', value: item.documentation }
      : undefined,
    insertText: item.insertText,
    insertTextFormat: item.insertTextRules?.includes('insert-as-snippet') ? 2 : 1,
    textEdit: {
      range: rangeToLSP(item.range),
      newText: item.insertText,
    },
    sortText: item.sortText,
    filterText: item.filterText,
    preselect: item.preselect,
    commitCharacters: item.commitCharacters,
    additionalTextEdits: (item.additionalTextEdits ?? []).map(edit => ({
      range: rangeToLSP(edit.range),
      newText: edit.text,
    })),
    command: item.command
      ? { title: item.command.title, command: item.command.id }
      : undefined,
  };
}

export function completionItemFromLSP(lspItem: Record<string, unknown>): Partial<CompletionItem> {
  const textEdit = lspItem['textEdit'] as Record<string, unknown> | undefined;
  const documentation = lspItem['documentation'];
  let docString: string | undefined;
  if (typeof documentation === 'string') {
    docString = documentation;
  } else if (documentation && typeof documentation === 'object') {
    docString = (documentation as Record<string, unknown>)['value'] as string;
  }

  return {
    label: lspItem['label'] as string,
    kind: completionKindFromLSP((lspItem['kind'] as number) ?? 1),
    detail: lspItem['detail'] as string | undefined,
    documentation: docString,
    insertText: (lspItem['insertText'] as string) ??
      (textEdit ? (textEdit['newText'] as string) : (lspItem['label'] as string)),
    sortText: lspItem['sortText'] as string | undefined,
    filterText: lspItem['filterText'] as string | undefined,
    preselect: lspItem['preselect'] as boolean | undefined,
    commitCharacters: lspItem['commitCharacters'] as string[] | undefined,
    range: textEdit
      ? rangeFromLSP(textEdit['range'] as LSPRange)
      : { start: { line: 0, column: 0 }, end: { line: 0, column: 0 } },
  };
}

// ═══ §5 SYMBOL MAPPING — symbol kinds, document symbols ═══

export const LSP_SYMBOL_KIND_MAP: ReadonlyMap<SymbolKind, number> = new Map([
  ['file', 1],
  ['module', 2],
  ['namespace', 3],
  ['package', 4],
  ['class', 5],
  ['method', 6],
  ['property', 7],
  ['field', 8],
  ['constructor', 9],
  ['enum', 10],
  ['interface', 11],
  ['function', 12],
  ['variable', 13],
  ['constant', 14],
  ['string', 15],
  ['number', 16],
  ['boolean', 17],
  ['array', 18],
  ['object', 19],
  ['key', 20],
  ['null', 21],
  ['enum-member', 22],
  ['struct', 23],
  ['event', 24],
  ['operator', 25],
  ['type-parameter', 26],
]);

const LSP_SYMBOL_KIND_REVERSE: ReadonlyMap<number, SymbolKind> = new Map(
  Array.from(LSP_SYMBOL_KIND_MAP.entries()).map(([k, v]) => [v, k]),
);

export function symbolKindToLSP(kind: SymbolKind): number {
  return LSP_SYMBOL_KIND_MAP.get(kind) ?? 13;
}

export function symbolKindFromLSP(lspKind: number): SymbolKind {
  return LSP_SYMBOL_KIND_REVERSE.get(lspKind) ?? 'variable';
}

export function documentSymbolToLSP(symbol: DocumentSymbol): Record<string, unknown> {
  return {
    name: symbol.name,
    detail: symbol.detail ?? '',
    kind: symbolKindToLSP(symbol.kind),
    range: rangeToLSP(symbol.range),
    selectionRange: rangeToLSP(symbol.selectionRange),
    children: (symbol.children ?? []).map(documentSymbolToLSP),
  };
}

export function documentSymbolFromLSP(lspSymbol: Record<string, unknown>): DocumentSymbol {
  const children = (lspSymbol['children'] as Record<string, unknown>[] | undefined) ?? [];
  return {
    name: lspSymbol['name'] as string,
    detail: (lspSymbol['detail'] as string) || undefined,
    kind: symbolKindFromLSP(lspSymbol['kind'] as number),
    range: rangeFromLSP(lspSymbol['range'] as LSPRange),
    selectionRange: rangeFromLSP(lspSymbol['selectionRange'] as LSPRange),
    children: children.length > 0 ? children.map(documentSymbolFromLSP) : undefined,
  };
}

// ═══ §6 EDIT MAPPING — text edits, workspace edits ═══

export interface LSPTextEdit {
  readonly range: LSPRange;
  readonly newText: string;
}

export function textEditToLSP(edit: EditOperation): LSPTextEdit {
  return {
    range: rangeToLSP(edit.range),
    newText: edit.text,
  };
}

export function textEditFromLSP(lspEdit: LSPTextEdit): Partial<EditOperation> {
  return {
    range: rangeFromLSP(lspEdit.range),
    text: lspEdit.newText,
  };
}

export function editToLSP(edit: EditOperation, uri: string): Record<string, unknown> {
  return {
    textDocument: { uri },
    edits: [textEditToLSP(edit)],
  };
}

export function editFromLSP(lspEdit: Record<string, unknown>): { uri: string; edits: Partial<EditOperation>[] } {
  const textDoc = lspEdit['textDocument'] as Record<string, unknown>;
  const edits = (lspEdit['edits'] as LSPTextEdit[]) ?? [];
  return {
    uri: textDoc['uri'] as string,
    edits: edits.map(textEditFromLSP),
  };
}

export function workspaceEditToLSP(
  changes: Map<string, EditOperation[]>,
): Record<string, unknown> {
  const lspChanges: Record<string, LSPTextEdit[]> = {};
  for (const [uri, edits] of changes) {
    lspChanges[uri] = edits.map(textEditToLSP);
  }
  return { changes: lspChanges };
}

export function workspaceEditFromLSP(
  lspWorkspaceEdit: Record<string, unknown>,
): Map<string, Partial<EditOperation>[]> {
  const result = new Map<string, Partial<EditOperation>[]>();
  const changes = lspWorkspaceEdit['changes'] as Record<string, LSPTextEdit[]> | undefined;
  if (changes) {
    for (const [uri, edits] of Object.entries(changes)) {
      result.set(uri, edits.map(textEditFromLSP));
    }
  }
  const docChanges = lspWorkspaceEdit['documentChanges'] as Record<string, unknown>[] | undefined;
  if (docChanges) {
    for (const dc of docChanges) {
      const textDoc = dc['textDocument'] as Record<string, unknown> | undefined;
      const edits = dc['edits'] as LSPTextEdit[] | undefined;
      if (textDoc && edits) {
        const uri = textDoc['uri'] as string;
        const existing = result.get(uri) ?? [];
        existing.push(...edits.map(textEditFromLSP));
        result.set(uri, existing);
      }
    }
  }
  return result;
}

// ═══ §7 HOVER & SIGNATURE — hover info and signature help mapping ═══

export function hoverToLSP(hover: HoverInfo): Record<string, unknown> {
  return {
    contents: hover.contents.map(c => ({ kind: 'markdown', value: c })),
    range: hover.range ? rangeToLSP(hover.range) : undefined,
  };
}

export function hoverFromLSP(lspHover: Record<string, unknown>): HoverInfo {
  const lspContents = lspHover['contents'];
  let contents: string[] = [];

  if (typeof lspContents === 'string') {
    contents = [lspContents];
  } else if (Array.isArray(lspContents)) {
    contents = lspContents.map((c: unknown) => {
      if (typeof c === 'string') return c;
      if (c && typeof c === 'object') return (c as Record<string, unknown>)['value'] as string;
      return String(c);
    });
  } else if (lspContents && typeof lspContents === 'object') {
    contents = [(lspContents as Record<string, unknown>)['value'] as string];
  }

  const lspRange = lspHover['range'] as LSPRange | undefined;
  return {
    contents,
    range: lspRange ? rangeFromLSP(lspRange) : undefined,
  };
}

export function parameterInfoToLSP(param: ParameterInfo): Record<string, unknown> {
  return {
    label: param.label,
    documentation: param.documentation
      ? { kind: 'markdown', value: param.documentation }
      : undefined,
  };
}

export function parameterInfoFromLSP(lspParam: Record<string, unknown>): ParameterInfo {
  const doc = lspParam['documentation'];
  let documentation: string | undefined;
  if (typeof doc === 'string') documentation = doc;
  else if (doc && typeof doc === 'object') documentation = (doc as Record<string, unknown>)['value'] as string;

  return {
    label: lspParam['label'] as string | readonly [number, number],
    documentation,
  };
}

export function signatureHelpToLSP(sig: SignatureHelp): Record<string, unknown> {
  return {
    signatures: sig.signatures.map(s => ({
      label: s.label,
      documentation: s.documentation
        ? { kind: 'markdown', value: s.documentation }
        : undefined,
      parameters: s.parameters.map(parameterInfoToLSP),
    })),
    activeSignature: sig.activeSignature,
    activeParameter: sig.activeParameter,
  };
}

export function signatureHelpFromLSP(lspSig: Record<string, unknown>): SignatureHelp {
  const sigs = (lspSig['signatures'] as Record<string, unknown>[]) ?? [];
  return {
    signatures: sigs.map(s => {
      const sigDoc = s['documentation'];
      let documentation: string | undefined;
      if (typeof sigDoc === 'string') documentation = sigDoc;
      else if (sigDoc && typeof sigDoc === 'object')
        documentation = (sigDoc as Record<string, unknown>)['value'] as string;

      return {
        label: s['label'] as string,
        documentation,
        parameters: ((s['parameters'] as Record<string, unknown>[]) ?? []).map(parameterInfoFromLSP),
      };
    }),
    activeSignature: (lspSig['activeSignature'] as number) ?? 0,
    activeParameter: (lspSig['activeParameter'] as number) ?? 0,
  };
}

// ═══ §8 CLIENT CAPABILITIES — initialization, server capabilities ═══

export const LSP_METHODS: Record<string, string> = {
  initialize: 'initialize',
  initialized: 'initialized',
  shutdown: 'shutdown',
  exit: 'exit',
  didOpen: 'textDocument/didOpen',
  didChange: 'textDocument/didChange',
  didClose: 'textDocument/didClose',
  didSave: 'textDocument/didSave',
  willSave: 'textDocument/willSave',
  completion: 'textDocument/completion',
  completionResolve: 'completionItem/resolve',
  hover: 'textDocument/hover',
  signatureHelp: 'textDocument/signatureHelp',
  definition: 'textDocument/definition',
  typeDefinition: 'textDocument/typeDefinition',
  implementation: 'textDocument/implementation',
  references: 'textDocument/references',
  documentHighlight: 'textDocument/documentHighlight',
  documentSymbol: 'textDocument/documentSymbol',
  codeAction: 'textDocument/codeAction',
  codeLens: 'textDocument/codeLens',
  codeLensResolve: 'codeLens/resolve',
  formatting: 'textDocument/formatting',
  rangeFormatting: 'textDocument/rangeFormatting',
  onTypeFormatting: 'textDocument/onTypeFormatting',
  rename: 'textDocument/rename',
  prepareRename: 'textDocument/prepareRename',
  foldingRange: 'textDocument/foldingRange',
  selectionRange: 'textDocument/selectionRange',
  semanticTokensFull: 'textDocument/semanticTokens/full',
  semanticTokensDelta: 'textDocument/semanticTokens/full/delta',
  inlayHint: 'textDocument/inlayHint',
  diagnostic: 'textDocument/diagnostic',
  publishDiagnostics: 'textDocument/publishDiagnostics',
  callHierarchyPrepare: 'textDocument/prepareCallHierarchy',
  callHierarchyIncoming: 'callHierarchy/incomingCalls',
  callHierarchyOutgoing: 'callHierarchy/outgoingCalls',
  typeHierarchyPrepare: 'textDocument/prepareTypeHierarchy',
  typeHierarchySupertypes: 'typeHierarchy/supertypes',
  typeHierarchySubtypes: 'typeHierarchy/subtypes',
  workspaceSymbol: 'workspace/symbol',
  didChangeConfiguration: 'workspace/didChangeConfiguration',
  didChangeWatchedFiles: 'workspace/didChangeWatchedFiles',
};

export function buildClientCapabilities(config: LSPClientConfig): Record<string, unknown> {
  const capabilities: Record<string, unknown> = {};

  if (config.capabilities.textDocument) {
    capabilities['textDocument'] = {
      synchronization: {
        dynamicRegistration: true,
        willSave: true,
        willSaveWaitUntil: true,
        didSave: true,
      },
      completion: {
        dynamicRegistration: true,
        completionItem: {
          snippetSupport: true,
          commitCharactersSupport: true,
          documentationFormat: ['markdown', 'plaintext'],
          deprecatedSupport: true,
          preselectSupport: true,
          insertReplaceSupport: true,
          resolveSupport: { properties: ['documentation', 'detail', 'additionalTextEdits'] },
        },
        contextSupport: true,
      },
      hover: {
        dynamicRegistration: true,
        contentFormat: ['markdown', 'plaintext'],
      },
      signatureHelp: {
        dynamicRegistration: true,
        signatureInformation: {
          documentationFormat: ['markdown', 'plaintext'],
          parameterInformation: { labelOffsetSupport: true },
        },
        contextSupport: true,
      },
      definition: { dynamicRegistration: true, linkSupport: true },
      typeDefinition: { dynamicRegistration: true, linkSupport: true },
      implementation: { dynamicRegistration: true, linkSupport: true },
      references: { dynamicRegistration: true },
      documentHighlight: { dynamicRegistration: true },
      documentSymbol: {
        dynamicRegistration: true,
        hierarchicalDocumentSymbolSupport: true,
      },
      codeAction: {
        dynamicRegistration: true,
        codeActionLiteralSupport: {
          codeActionKind: {
            valueSet: [
              'quickfix', 'refactor', 'refactor.extract', 'refactor.inline',
              'refactor.rewrite', 'source', 'source.organizeImports', 'source.fixAll',
            ],
          },
        },
        isPreferredSupport: true,
        resolveSupport: { properties: ['edit'] },
      },
      codeLens: { dynamicRegistration: true },
      formatting: { dynamicRegistration: true },
      rangeFormatting: { dynamicRegistration: true },
      onTypeFormatting: { dynamicRegistration: true },
      rename: { dynamicRegistration: true, prepareSupport: true },
      foldingRange: { dynamicRegistration: true, lineFoldingOnly: true },
      selectionRange: { dynamicRegistration: true },
      semanticTokens: {
        dynamicRegistration: true,
        tokenTypes: [
          'namespace', 'type', 'class', 'enum', 'interface', 'struct',
          'typeParameter', 'parameter', 'variable', 'property', 'enumMember',
          'event', 'function', 'method', 'macro', 'keyword', 'modifier',
          'comment', 'string', 'number', 'regexp', 'operator', 'decorator',
        ],
        tokenModifiers: [
          'declaration', 'definition', 'readonly', 'static', 'deprecated',
          'abstract', 'async', 'modification', 'documentation', 'defaultLibrary',
        ],
        formats: ['relative'],
        requests: { full: { delta: true } },
        multilineTokenSupport: false,
        overlappingTokenSupport: false,
      },
      inlayHint: { dynamicRegistration: true },
      diagnostic: { dynamicRegistration: true },
      publishDiagnostics: {
        relatedInformation: true,
        tagSupport: { valueSet: [1, 2] },
        versionSupport: true,
        codeDescriptionSupport: true,
      },
      callHierarchy: { dynamicRegistration: true },
      typeHierarchy: { dynamicRegistration: true },
    };
  }

  if (config.capabilities.workspace) {
    capabilities['workspace'] = {
      applyEdit: true,
      workspaceEdit: { documentChanges: true },
      didChangeConfiguration: { dynamicRegistration: true },
      didChangeWatchedFiles: { dynamicRegistration: true },
      symbol: {
        dynamicRegistration: true,
        symbolKind: { valueSet: Array.from({ length: 26 }, (_, i) => i + 1) },
      },
      configuration: true,
      workspaceFolders: true,
    };
  }

  if (config.capabilities.window) {
    capabilities['window'] = {
      workDoneProgress: true,
      showMessage: { messageActionItem: { additionalPropertiesSupport: true } },
      showDocument: { support: true },
    };
  }

  if (config.capabilities.general) {
    capabilities['general'] = {
      staleRequestSupport: {
        cancel: true,
        retryOnContentModified: [
          'textDocument/semanticTokens/full',
          'textDocument/semanticTokens/full/delta',
        ],
      },
      positionEncodings: ['utf-16'],
    };
  }

  return capabilities;
}

export function buildInitializationOptions(config: LSPClientConfig): Record<string, unknown> {
  return {
    ...(config.initializationOptions as Record<string, unknown> | undefined),
  };
}

export function parseServerCapabilities(capabilities: Record<string, unknown>): Record<string, boolean> {
  const features: Record<string, boolean> = {};
  features['completion'] = capabilities['completionProvider'] !== undefined;
  features['hover'] = capabilities['hoverProvider'] === true || typeof capabilities['hoverProvider'] === 'object';
  features['signatureHelp'] = capabilities['signatureHelpProvider'] !== undefined;
  features['definition'] = capabilities['definitionProvider'] === true || typeof capabilities['definitionProvider'] === 'object';
  features['typeDefinition'] = capabilities['typeDefinitionProvider'] === true || typeof capabilities['typeDefinitionProvider'] === 'object';
  features['implementation'] = capabilities['implementationProvider'] === true || typeof capabilities['implementationProvider'] === 'object';
  features['references'] = capabilities['referencesProvider'] === true || typeof capabilities['referencesProvider'] === 'object';
  features['documentHighlight'] = capabilities['documentHighlightProvider'] === true || typeof capabilities['documentHighlightProvider'] === 'object';
  features['documentSymbol'] = capabilities['documentSymbolProvider'] === true || typeof capabilities['documentSymbolProvider'] === 'object';
  features['codeAction'] = capabilities['codeActionProvider'] === true || typeof capabilities['codeActionProvider'] === 'object';
  features['codeLens'] = capabilities['codeLensProvider'] !== undefined;
  features['formatting'] = capabilities['documentFormattingProvider'] === true || typeof capabilities['documentFormattingProvider'] === 'object';
  features['rangeFormatting'] = capabilities['documentRangeFormattingProvider'] === true || typeof capabilities['documentRangeFormattingProvider'] === 'object';
  features['rename'] = capabilities['renameProvider'] === true || typeof capabilities['renameProvider'] === 'object';
  features['foldingRange'] = capabilities['foldingRangeProvider'] === true || typeof capabilities['foldingRangeProvider'] === 'object';
  features['selectionRange'] = capabilities['selectionRangeProvider'] === true || typeof capabilities['selectionRangeProvider'] === 'object';
  features['semanticTokens'] = capabilities['semanticTokensProvider'] !== undefined;
  features['inlayHint'] = capabilities['inlayHintProvider'] === true || typeof capabilities['inlayHintProvider'] === 'object';
  features['callHierarchy'] = capabilities['callHierarchyProvider'] === true || typeof capabilities['callHierarchyProvider'] === 'object';
  features['typeHierarchy'] = capabilities['typeHierarchyProvider'] === true || typeof capabilities['typeHierarchyProvider'] === 'object';
  features['workspaceSymbol'] = capabilities['workspaceSymbolProvider'] === true || typeof capabilities['workspaceSymbolProvider'] === 'object';
  features['diagnostic'] = capabilities['diagnosticProvider'] !== undefined;
  return features;
}

export function isMethodSupported(capabilities: Record<string, boolean>, method: string): boolean {
  const featureKey = method.replace('textDocument/', '').replace('workspace/', '');
  return capabilities[featureKey] === true;
}

// ═══ §9 REQUEST BUILDING — construct LSP notification/request payloads ═══

export function buildDidOpenRequest(
  uri: string,
  languageId: string,
  version: number,
  content: string,
): Record<string, unknown> {
  return {
    method: LSP_METHODS['didOpen'],
    params: {
      textDocument: {
        uri,
        languageId,
        version,
        text: content,
      },
    },
  };
}

export function buildDidChangeRequest(
  uri: string,
  version: number,
  changes: Array<{ range?: Range; text: string }>,
): Record<string, unknown> {
  return {
    method: LSP_METHODS['didChange'],
    params: {
      textDocument: { uri, version },
      contentChanges: changes.map(c =>
        c.range
          ? { range: rangeToLSP(c.range), text: c.text }
          : { text: c.text },
      ),
    },
  };
}

export function buildDidCloseRequest(uri: string): Record<string, unknown> {
  return {
    method: LSP_METHODS['didClose'],
    params: {
      textDocument: { uri },
    },
  };
}

export function buildCompletionRequest(
  uri: string,
  position: Position,
  context?: CompletionContext,
): Record<string, unknown> {
  return {
    method: LSP_METHODS['completion'],
    params: {
      textDocument: { uri },
      position: positionToLSP(position),
      context: context
        ? {
            triggerKind: completionTriggerToLSP(context.triggerKind),
            triggerCharacter: context.triggerCharacter,
          }
        : undefined,
    },
  };
}

export function buildHoverRequest(
  uri: string,
  position: Position,
): Record<string, unknown> {
  return {
    method: LSP_METHODS['hover'],
    params: {
      textDocument: { uri },
      position: positionToLSP(position),
    },
  };
}

export function buildDefinitionRequest(
  uri: string,
  position: Position,
): Record<string, unknown> {
  return {
    method: LSP_METHODS['definition'],
    params: {
      textDocument: { uri },
      position: positionToLSP(position),
    },
  };
}

export function buildReferencesRequest(
  uri: string,
  position: Position,
  includeDecl?: boolean,
): Record<string, unknown> {
  return {
    method: LSP_METHODS['references'],
    params: {
      textDocument: { uri },
      position: positionToLSP(position),
      context: { includeDeclaration: includeDecl ?? true },
    },
  };
}

export function buildFormattingRequest(
  uri: string,
  options: FormatOptions,
): Record<string, unknown> {
  return {
    method: LSP_METHODS['formatting'],
    params: {
      textDocument: { uri },
      options: {
        tabSize: options.tabSize,
        insertSpaces: options.insertSpaces,
        trimTrailingWhitespace: options.trimAutoWhitespace ?? false,
        insertFinalNewline: options.insertFinalNewline ?? false,
        trimFinalNewlines: options.trimFinalNewlines ?? false,
      },
    },
  };
}

export function buildRenameRequest(
  uri: string,
  position: Position,
  newName: string,
): Record<string, unknown> {
  return {
    method: LSP_METHODS['rename'],
    params: {
      textDocument: { uri },
      position: positionToLSP(position),
      newName,
    },
  };
}
